import { Subscription } from 'rxjs';
import { Menu } from 'primeng/menu';

export interface MenuAboveOptions {
  // Which edges of the menu and the anchor line up
  align: 'left' | 'right';
  // Gap between the anchor and the menu above it, in px
  gap?: number;
}

type PageScroll = { element: Element; top: number; left: number }[];

interface OpenMenu {
  anchor: HTMLElement;
  options: MenuAboveOptions;
  shown?: Subscription;
  hidden?: Subscription;
  observer?: ResizeObserver;
}

/**
 * Opens PrimeNG popup menus above an anchor at the bottom of the chat: the composer's paperclip, / commands and
 * @ mentions menus.
 *
 * PrimeNG puts a popup menu below its target unless it measures no room there, but it measures the menu while it is
 * 0px tall - it animates open from 0px, with the items hanging out below - so it always finds room. It then focuses
 * the menu, and the browser scrolls the page to bring the items into view, even a body with overflow hidden. That moves
 * the whole chat under the menu and leaves it moved after the menu closes.
 */
export class MenuHelpers {
  private static openMenus = new WeakMap<Menu, OpenMenu>();

  /**
   * Shows the menu above the anchor, or moves it there if it's already open (e.g. the mentions list for the next
   * typed letter). It then follows the anchor and its own items as they change size, until it closes.
   */
  static showAbove(menu: Menu, anchor: HTMLElement, options: MenuAboveOptions) {
    const open = this.openMenus.get(menu);
    if (menu.visible && open?.observer) {
      open.anchor = anchor;
      open.options = options;
      menu.show({ currentTarget: anchor });
      this.positionAbove(menu, open);
      return;
    }
    this.release(menu);
    const pageScroll = this.savePageScroll();
    const next: OpenMenu = { anchor, options };
    this.openMenus.set(menu, next);
    // Fires as PrimeNG opens it, right after its focus has scrolled the page and before anything is painted
    next.shown = menu.onShow.subscribe(() => {
      next.shown.unsubscribe();
      this.restorePageScroll(pageScroll);
      this.positionAbove(menu, next);
      // Delivers a first size before the next paint, so this also catches items that render after onShow
      next.observer = new ResizeObserver(() => this.positionAbove(menu, next));
      next.observer.observe(anchor);
      const list = menu.listViewChild()?.nativeElement;
      if (list) {
        next.observer.observe(list);
      }
      next.hidden = menu.onHide.subscribe(() => this.release(menu));
    });
    menu.show({ currentTarget: anchor });
  }

  private static release(menu: Menu) {
    const open = this.openMenus.get(menu);
    open?.shown?.unsubscribe();
    open?.hidden?.unsubscribe();
    open?.observer?.disconnect();
    this.openMenus.delete(menu);
  }

  private static savePageScroll(): PageScroll {
    return [document.documentElement, document.body].map(element => ({
      element,
      top: element.scrollTop,
      left: element.scrollLeft
    }));
  }

  private static restorePageScroll(pageScroll: PageScroll) {
    pageScroll.forEach(({ element, top, left }) => {
      element.scrollTop = top;
      element.scrollLeft = left;
    });
  }

  // Placed by its top edge: PrimeNG's container stays 0px tall while it animates open, so its own height and bottom edge
  // say nothing - scrollHeight takes in the items. Fixed is safe: PrimeNG closes the menu on scroll and resize.
  private static positionAbove(menu: Menu, { anchor, options }: OpenMenu) {
    const container = menu.container as HTMLElement;
    if (!container) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const height = Math.max(container.offsetHeight, container.scrollHeight);
    const viewportWidth = document.documentElement.clientWidth;
    const width = container.offsetWidth;
    // PrimeNG's own placement: its logical inset would fight the physical one below, its margin is the gap below the target
    container.style.removeProperty('inset-inline-start');
    container.style.removeProperty('inset-inline-end');
    Object.assign(container.style, {
      position: 'fixed',
      top: `${Math.max(rect.top - (options.gap ?? 0) - height, 0)}px`,
      bottom: 'auto',
      marginTop: '0',
      // The open animation scales from this corner, so it never moves the edges lined up with the anchor
      transformOrigin: `bottom ${options.align}`
    });
    if (options.align === 'right') {
      Object.assign(container.style, { left: 'auto', right: `${Math.max(viewportWidth - rect.right, 0)}px` });
    } else {
      Object.assign(container.style, {
        right: 'auto',
        left: `${Math.max(Math.min(rect.left, viewportWidth - width), 0)}px`
      });
    }
  }
}
